import { EventEmitter } from 'eventemitter3'

import { sanitize } from 'core-components/helpers/sanitize'
import { getTokens } from 'src/core/classes/helpers/StorageHelper'
import { authService } from 'src/core/classes/services/AuthService'
import Logger from 'src/core/utils/Logger'
import { getTokenExpirationTime } from 'src/core/utils/Token'

import { guid } from '../helpers/GuidHelper'

import { EWSEvent, IWSClient } from './IWSClient'
import { Ready } from './Ready'

const types = ['response', 'event', 'error'] as ['response', 'event', 'error']
type ResponseType = (typeof types)[number]

interface IRequestHeader {
  uuid: string
  service: string
  action: string
}

interface IResponseHeader {
  uuid: string
  service: string
  action: string
  name?: string
  type?: ResponseType
}

export interface TResult<T extends Record<string, any>> {
  status: 'success' | 'error' | 'fail'
  data: T
  error?: string
}

export interface TResponse<T extends Record<string, any>> {
  header: IResponseHeader
  payload: TResult<T>
}

export interface TRequest {
  resolve: (payload: TResult<any> | PromiseLike<TResult<any>>) => void
  reject: (payload: Error) => void
  header: IRequestHeader
  payload: Record<string, any>
  returnError: boolean
  responseLength?: number
  start: number
  ttl?: number
}

export class WSClient implements IWSClient {
  public bus: EventEmitter = new EventEmitter()
  public isConnectionOpen = new Ready()
  public isConnectionClosed = new Ready()
  public isConnected = false
  public isAuthorized = new Ready()
  public ws: WebSocket | null = null

  private readonly debug: boolean = false
  private readonly wsUrl: string
  private readonly requestTimeoutMS: number

  private isTokenUpdating = false
  private isReconnect = false
  private shouldReconnect: boolean = true
  private requests: Map<string, any> = new Map()
  private delayAttempt: number = 1000
  private dropRequestIntervalId: number | null = null
  private waitForSocketStateTimeoutId: number | null = null
  private requestsQueueCheckTimerId: number | null = null

  /**
   */
  public constructor(
    wsUrl: string,
    reconnect: boolean = true,
    requestTimeoutMS: number = 60000,
    debug = false
  ) {
    this.requestTimeoutMS = requestTimeoutMS
    this.shouldReconnect = reconnect
    this.debug = debug
    this.wsUrl = wsUrl
    this.setDropRequestsInterval()
  }

  public async connect(): Promise<void> {
    if (!this.ws || this.ws.readyState === this.ws.CLOSED) {
      this.establishConnect()
    }
    const result = await this.isConnectionOpen.isResolved()
    if (result) {
      Logger.info(`[${this.wsUrl}] Connection was established`)
    } else {
      throw new Error(`[${this.wsUrl}] Connection was not established`)
    }
  }

  private establishConnect() {
    this.ws = new WebSocket(this.wsUrl)
    this.ws.addEventListener('error', this.handleError)
    this.ws.addEventListener('open', this.handleOpen)
    this.ws.addEventListener('close', this.handleClose)
    this.ws.addEventListener('message', this.handleMessage)
  }

  public modifyResponse = async (
    response: TResponse<Record<string, any>>,
    request?: TRequest
  ): Promise<void> => {}

  /**
   */
  public async call<T = any>(
    service: string,
    action: string,
    payload: any,
    returnError: boolean = false
  ): Promise<T> {
    await this.isConnectionOpen.isResolved()
    const uuid = guid()
    const header = { uuid, service, action }
    const start = Date.now()
    this.send(header, payload).catch(Logger.error)

    return new Promise((resolve, reject) => {
      const ttl = this.requestTimeoutMS ? start + this.requestTimeoutMS : undefined
      this.requests.set(uuid, {
        resolve,
        reject,
        header,
        payload,
        returnError,
        start,
        ttl,
      })
    })
  }

  public async disconnect() {
    this.shouldReconnect = false
    if (this.debug) {
      Logger.debug(`[${this.wsUrl}] Connection terminating`)
    }

    this.isConnected = false
    if (this.ws) {
      this.ws.close()
      await this.isConnectionClosed.isResolved()
    }

    this.bus.emit(EWSEvent.terminated)
  }

  /**
   */
  public on(event: EWSEvent | string, handler: (payload: Record<string, any>) => void) {
    this.bus.on(event, handler)
    return () => {
      return this.bus.off && this.bus.off(event, handler)
    }
  }

  public async isRequestsQueueEmpty() {
    const result = await new Promise((resolve) => {
      this.requestsQueueCheckTimerId = window.setInterval(() => {
        if (this.requests.size === 0) {
          Logger.info(`[${this.wsUrl}] queue is empty`)
          resolve(true)
          return
        }
        Logger.info(`[${this.wsUrl}] queue is not empty`)
      }, 1000)
    })

    if (this.requestsQueueCheckTimerId) {
      clearInterval(this.requestsQueueCheckTimerId)
    }

    return result
  }

  /**
   */
  private async send(header: IRequestHeader, payload: Record<string, any> = {}) {
    const message = JSON.stringify({ header, payload })

    if (this.ws) {
      await this.waitForSocketState(this.ws, this.ws.OPEN)
      this.ws.send(message)
    }
  }

  /**
   */
  private handleOpen = (event: Event) => {
    this.delayAttempt = 1000
    this.isConnected = true
    this.isConnectionClosed.reset()
    this.isConnectionOpen.resolve()
    this.bus.emit(EWSEvent.open)
    if (this.isReconnect) {
      this.bus.emit(EWSEvent.reconnect)
    }
  }

  /**
   */
  private handleError = (event: any) => {
    Logger.error(event)
    this.bus.emit(EWSEvent.error, event)
  }

  /**
   */
  private handleClose = async (event: CloseEvent) => {
    this.isAuthorized.reset()

    if (this.debug) {
      Logger.debug(`[${this.wsUrl}] Connection closing`)
    }

    this.isConnected = false

    if (this.ws) {
      await this.waitForSocketState(this.ws, this.ws.CLOSED)
      if (this.debug) {
        Logger.debug(`[${this.wsUrl}] Connection closed`)
      }
    }

    this.bus.emit(EWSEvent.close)
    this.clearEvents()

    if (!this.shouldReconnect) {
      this.isConnectionClosed.resolve()
    }

    this.reconnect()
  }

  private reconnect() {
    if (this.shouldReconnect) {
      Logger.debug(`[${this.wsUrl}] Connection reconnect in ${this.delayAttempt}ms`)
      this.isReconnect = true
      setTimeout(() => this.establishConnect(), this.delayAttempt)
      if (this.delayAttempt < 10000) {
        this.delayAttempt += 3000
      }
    }
  }

  /**
   */
  private handleMessage = async (event: MessageEvent) => {
    try {
      if (typeof event.data !== 'string') {
        throw new Error(`[${this.wsUrl}] Unsupported data type`)
      }

      const responseLength = event.data.length
      const data: TResponse<Record<string, any>> = JSON.parse(event.data) || {}
      if (data.header) {
        const { uuid } = data.header
        await this.modifyResponse(data, this.requests.get(uuid))
        const payload = data.payload || {}
        const { status } = payload

        const error = ['error', 'fail'].includes(status) ? payload.error || 'System Error' : null

        const request = this.requests.get(uuid)
        const returnError = request && request.returnError

        if (error && returnError) {
          if (request) {
            const { header } = request
            Logger.error(`[${this.wsUrl}] handleMessage`, {
              request: { header, payload: request.payload },
              response: { payload, header: data.header },
            })

            if (data.header.type === 'response') {
              this.processDebug(request)
              this.resolveRequest(data).catch(Logger.error)
            } else {
              request.reject(new Error(error))
            }

            this.requests.delete(uuid)
          }
        } else if (data.header.type === 'event') {
          const eventName = `${data.header.name}`
          this.processEventDebug(eventName, payload)
          setImmediate(() => this.bus.emit(eventName, payload))
        } else if (data.header.type === 'response' && request) {
          request.responseLength = responseLength
          this.processDebug(request)
          this.resolveRequest(data).catch(Logger.error)
        }
      } else {
        throw new Error(`[${this.wsUrl}] Incorrect response`)
      }
    } catch (error) {
      Logger.error(sanitize(error))
    }
  }

  /**
   */
  private processDebug = (request: TRequest) => {
    if (this.debug) {
      const key = JSON.stringify(request.header)
      const time = Date.now() - request.start
      Logger.info(
        `[${this.wsUrl}] `,
        key,
        `Time: ${time} ms, Length: ${Math.round((request.responseLength || 0) / 1024)} kb`
      )
    }
  }

  private log = (groupName: string, data: any) => {
    Logger.groupCollapsed(`${groupName}`)
    Object.keys(data).forEach((k) => Logger.info(k, JSON.parse(JSON.stringify(data[k]))))
    try {
      Logger.info(
        `Time: ${data.response.date - data.request.date} ms, Length: ${Math.round(
          (data.request.responseLength || 0) / 1024
        )} kb`
      )
    } catch (e) {
      Logger.error(e)
    }
    Logger.groupEnd()
  }

  /**
   */
  private processEventDebug = (event: string, payload: Record<string, any>) => {
    if (this.debug) {
      const data = JSON.stringify(payload)
      Logger.info(`[${this.wsUrl}]`, event, data)
    }
  }

  /**
   */
  private resolveRequest = async (data: Record<string, any>) => {
    const {
      payload = {},
      header: { uuid },
    } = data

    if (!payload.data) {
      payload.data = {}
    }

    const request = this.requests.get(uuid)

    if (request) {
      const key = `${request.header.service}:${request.header.action}`
      this.log(`${new Date().toISOString()} [${this.wsUrl}]: ${key}`, {
        request: {
          header: request.header,
          payload: request.payload,
          date: request.start,
          responseLength: request.responseLength,
        },
        response: { payload, header: data.header, date: new Date() },
      })

      request.resolve(payload)
      this.requests.delete(uuid)
    }
  }

  /**
   */
  private clearEvents = () => {
    if (this.ws) {
      this.ws.removeEventListener('error', this.handleError)
      this.ws.removeEventListener('open', this.handleOpen)
      this.ws.removeEventListener('close', this.handleClose)
      this.ws.removeEventListener('message', this.handleMessage)
      if (this.dropRequestIntervalId) {
        clearInterval(this.dropRequestIntervalId)
      }

      if (this.waitForSocketStateTimeoutId) {
        clearTimeout(this.waitForSocketStateTimeoutId)
      }

      if (this.requestsQueueCheckTimerId) {
        clearInterval(this.requestsQueueCheckTimerId)
      }
    }
  }

  private setDropRequestsInterval() {
    if (this.requestTimeoutMS) {
      this.dropRequestIntervalId = window.setInterval(() => {
        this.dropRequests()
      }, 1000)
    }
  }

  private dropRequests() {
    for (const [uuid, request] of this.requests) {
      if (request.ttl && Date.now() >= request.ttl) {
        Logger.error('Timeout', request)
        request.reject(new Error('Timeout'))
        this.requests.delete(uuid)
      }
    }
  }

  private waitForSocketState(socket: WebSocket, state: number) {
    return new Promise((resolve, reject) => {
      this.waitForSocketStateTimeoutId = window.setTimeout(() => {
        if (socket.readyState === state) {
          resolve(true)
        } else {
          this.waitForSocketState(socket, state).then(resolve).catch(reject)
        }
      }, 5)
    })
  }
}
